import type {
    MultiAssetValuationDataPoint,
    MultiAssetValueOverviewSummary,
    NormalizedMultiAssetValuationHistory,
    SingleAssetValueOverview,
} from '@einwert/valuation-api-client'

import { Box, Stack } from '@mui/material'
import MultiAssetMarketValueGraph, { type AnalysisGraphMetric } from './components/MultiAssetMarketValueGraph'
import { CardItem, InfoTooltip, LoadingSkeleton, MetricGrid, MonetaryValue, Percentage, Tab, TabBar, tokens } from 'ComponentLibrary'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'

type MarketValueAnalysisProps = {
    assets: SingleAssetValueOverview[]
    summary?: MultiAssetValueOverviewSummary
    history?: MultiAssetValuationDataPoint[]
    normalizedHistory?: NormalizedMultiAssetValuationHistory
    isFilteredSummaryPending: boolean
}

const MarketValueAnalysis = ({ assets, summary, history, normalizedHistory, isFilteredSummaryPending }: MarketValueAnalysisProps) => {
    const { t } = useTranslation('pages', { keyPrefix: 'analysis.analysisPage' })
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const locale = useCurrentResolvedLocale()
    const [metric, setMetric] = useState<AnalysisGraphMetric>('market_value')
    const metricSummary = summary?.[metric]
    const metricValueText = MonetaryValue.fromMonetaryValueDataObject(metricSummary?.value).format({ withoutDecimals: true, locale })
    const trendText = metricSummary?.trend !== undefined ? new Percentage(metricSummary.trend).format({ withSign: true, locale }) : '-'
    const isMarketValue = metric === 'market_value'

    return (
        <Stack direction="column" width="100%" position="sticky" top="0px" gap={tokens.SizingSpacing100}>
            <Box marginBottom={tokens.SizingSpacing150}>
                <TabBar<AnalysisGraphMetric>
                    type="stretched"
                    selectedTab={metric}
                    handleChange={(newMetric) => setMetric(newMetric)}
                    ariaLabel={t('metricTabsAriaLabel')}
                >
                    <Tab value="market_value" label={tCommon('marketValue')} />
                    <Tab value="rental_income" label={tCommon('marketRent')} />
                </TabBar>
            </Box>
            <Stack direction="column" height="589px" gap={tokens.SizingSpacing300}>
                <CardItem>
                    {isFilteredSummaryPending ? (
                        <LoadingSkeleton variant="rounded" height="108px" width="100%" />
                    ) : (
                        <MetricGrid
                            numColumns={2}
                            size="small"
                            values={[
                                {
                                    value: metricValueText,
                                    subheadline: t(
                                        isMarketValue ? 'metrics.totalSelectedAssetsMarketValue' : 'metrics.totalSelectedAssetsMarketRent'
                                    ),
                                },
                                {
                                    value: trendText,
                                    subheadline: t(isMarketValue ? 'metrics.selectedAssetsValueTrend' : 'metrics.selectedAssetsRentTrend'),
                                    endAdornment: (
                                        <InfoTooltip
                                            text={t(isMarketValue ? 'marketValueTrendTooltip' : 'marketRentTrendTooltip')}
                                            style={{ alignSelf: 'flex-start', marginTop: tokens.SizingSpacing100 }}
                                        />
                                    ),
                                },
                            ]}
                        />
                    )}
                </CardItem>
                <CardItem display="flex" flexDirection="column" flexGrow={1} minHeight={0}>
                    <MultiAssetMarketValueGraph
                        assets={assets}
                        aggregatedHistory={history || []}
                        normalizedHistory={normalizedHistory}
                        metric={metric}
                    />
                </CardItem>
            </Stack>
        </Stack>
    )
}

export default MarketValueAnalysis
